import {inject, Injectable, InjectionToken, Signal, signal, WritableSignal} from "@angular/core";

import * as Models from "app/services/proxy/model/models";
import {FeatureFlagsHelper} from "app/services/third-party/feature-flags/feature-flags-helper.service";

import {Lookup} from "framework/services/utils.service";

export const FEATURE_FLAGS_PROVIDER = new InjectionToken<IFeatureFlagsProvider>("IFeatureFlagsProvider");

@Injectable()
export class FeatureFlagsService
{
    private readonly m_provider = inject(FEATURE_FLAGS_PROVIDER);

    private readonly m_releaseFlagsState = signal<Lookup<Models.ReleaseFlag>>(
        FeatureFlagsHelper.buildDefaultsLookup(new Models.ReleaseFlagsWithDefaults()) as Lookup<Models.ReleaseFlag>
    );
    private readonly m_featureFlagsState = signal<Lookup<Models.FeatureFlag>>(
        FeatureFlagsHelper.buildDefaultsLookup(
            new Models.AiFeatureFlagsWithDefaults(),
            new Models.AiAgentFeatureFlagsWithDefaults(),
            new Models.BrokerFeatureFlagsWithDefaults(),
            new Models.ComplianceFeatureFlagsWithDefaults(),
            new Models.CostFeatureFlagsWithDefaults(),
            new Models.GovernanceFeatureFlagsWithDefaults(),
            new Models.HeatstackFeatureFlagsWithDefaults(),
            new Models.IdentityFeatureFlagsWithDefaults(),
            new Models.OperationsFeatureFlagsWithDefaults(),
            new Models.OthersFeatureFlagsWithDefaults(),
            new Models.ReportGenerationFeatureFlagsWithDefaults(),
            new Models.ReportsFeatureFlagsWithDefaults(),
            new Models.ResourceInventoryFeatureFlagsWithDefaults(),
            new Models.SecurityGovernanceFeatureFlagsWithDefaults(),
            new Models.UiFeatureFlagsWithDefaults()
        ) as Lookup<Models.FeatureFlag>
    );

    public readonly releaseFlagsState: Signal<Lookup<Models.ReleaseFlag>> = this.m_releaseFlagsState.asReadonly();
    public readonly featureFlagsState: Signal<Lookup<Models.FeatureFlag>> = this.m_featureFlagsState.asReadonly();

    constructor()
    {
        this.m_provider.setReleaseFlagsState(this.m_releaseFlagsState);
        this.m_provider.setFeatureFlagsState(this.m_featureFlagsState);
    }

    public initializeProvider(context: Models.FeatureFlagContext): void
    {
        this.m_provider.initializeProvider(context);
    }

    public queueFlag(request: Models.FeatureFlagRequestBase): Promise<void>
    {
        return this.m_provider.queueFlag(request);
    }

    public queueFlags(request: Models.FeatureFlagRequestBase): Promise<void>
    {
        return this.m_provider.queueFlags(request);
    }

    public isEnabled(flagKey: string): boolean
    {
        const flag = this.m_featureFlagsState()[flagKey];
        return flag?.enabled ?? flag?.default ?? false;
    }

}

export interface IFeatureFlagsProvider
{
    initializeProvider(context: Models.FeatureFlagContext): void;

    setReleaseFlagsState(state: WritableSignal<Lookup<Models.ReleaseFlag>>): void;

    setFeatureFlagsState(state: WritableSignal<Lookup<Models.FeatureFlag>>): void;

    queueFlag(request: Models.FeatureFlagRequestBase): Promise<void>;

    queueFlags(request: Models.FeatureFlagRequestBase): Promise<void>;
}
